import { useState } from "react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MANAGE_ROLES = ["ADMIN", "SALES_MANAGER"];

export function FlooringSystemSelect({ value, onChange, testId, placeholder = "Select system", triggerClassName = "" }) {
  const { user, meta, loadMeta } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const items = meta?.flooring_systems || [];
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // name pending delete

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const addNew = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/meta/flooring-systems", { name });
      await loadMeta();
      toast.success(`Added "${name}"`);
      onChange?.(name);
      setNewName("");
      setAdding(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add system");
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (name) => {
    setBusy(true);
    try {
      await api.delete(`/meta/flooring-systems/${encodeURIComponent(name)}`);
      await loadMeta();
      toast.success(`Removed "${name}"`);
      if (value === name) onChange?.("");
      setConfirm(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove system");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={triggerClassName}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-[320px]">
        <SelectGroup>
          {items.map((name) => (
            <div key={name} className="relative flex items-center">
              <SelectItem value={name} className={canManage ? "pr-9 flex-1" : "flex-1"}>{name}</SelectItem>
              {canManage && (
                <button
                  type="button"
                  onPointerDown={stop}
                  onMouseDown={stop}
                  onClick={(e) => { stop(e); setConfirm(name); }}
                  disabled={busy}
                  data-testid={`flooring-remove-${name}`}
                  title="Remove this system"
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {items.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-slate-400">No flooring systems yet{canManage ? ". Add one below." : "."}</div>
          )}
        </SelectGroup>

        {canManage && (
          <>
            <SelectSeparator />
            <div
              className="p-2"
              onPointerDown={stop}
              onMouseDown={stop}
              onClick={stop}
              onKeyDown={(e) => e.stopPropagation()}>
              {adding ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") { e.preventDefault(); addNew(); }
                      else if (e.key === "Escape") { setAdding(false); setNewName(""); }
                    }}
                    placeholder="e.g. Dazlac PU Matte"
                    data-testid="flooring-new-input"
                    className="h-8 text-sm" />
                  <Button size="sm" onClick={addNew} disabled={busy || !newName.trim()} data-testid="flooring-new-save" className="h-8 px-2">
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewName(""); }} className="h-8 px-2">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  data-testid="flooring-add-btn"
                  className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium text-sky-700 hover:bg-sky-50">
                  <Plus className="h-3.5 w-3.5" /> Add new flooring system
                </button>
              )}
            </div>
          </>
        )}
      </SelectContent>

      {confirm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
          onClick={() => !busy && setConfirm(null)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={stop}>
            <div className="text-base font-semibold text-slate-900">Remove flooring system?</div>
            <div className="mt-2 text-sm text-slate-600">
              <span className="font-mono text-slate-900">{confirm}</span> will no longer appear in the dropdown. Existing leads/quotations keep their saved value.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
              <Button onClick={() => removeItem(confirm)} disabled={busy} data-testid="flooring-remove-confirm" className="bg-red-600 text-white hover:bg-red-700">
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </Select>
  );
}
